import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Camera, Utensils, Hotel, Lock, Star, 
  ChevronRight, Clock, Lightbulb, Map as MapIcon,
  ShoppingBag, CheckCircle, ArrowRight, Calendar,
  ClipboardList, ChevronDown
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { TipCard, ItineraryCard, PlaceCard } from './GuidePremium';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simplified PlaceCard for preview
const PreviewPlaceCard = ({ place, language, isLocked = false }) => {
  const name = language === 'it' ? place.name_it : place.name_en;
  const description = language === 'it' ? place.description_it : place.description_en;
  
  const categoryIcons = {
    sightseeing: Camera,
    restaurant: Utensils,
    hotel: Hotel,
    activity: MapPin
  };
  const Icon = categoryIcons[place.category] || MapPin;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`group bg-[#2C2C2E] rounded-2xl overflow-hidden shadow-sm border border-[#3A3A3C] ${isLocked ? 'opacity-60' : ''}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img 
          src={place.image_url} 
          alt={name}
          className={`w-full h-full object-cover transition-transform duration-500 ${isLocked ? 'blur-sm' : 'group-hover:scale-105'}`}
        />
        <div className="absolute top-3 left-3 bg-[#2C2C2E]/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <Icon className="w-4 h-4 text-[#C9B896]" />
          <span className="text-xs font-medium text-[#F5F5F7] capitalize">{place.category}</span>
        </div>
        {isLocked && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Lock className="w-10 h-10 text-white/80" />
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-lg mb-2">
          {isLocked ? '••••••••' : name}
        </h3>
        <p className={`text-[#86868B] text-sm line-clamp-3 ${isLocked ? 'blur-sm select-none' : ''}`}>
          {isLocked ? 'Lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor.' : description}
        </p>
      </div>
    </motion.div>
  );
};

// Simplified TipCard for preview
const PreviewTipCard = ({ tip, language, isLocked = false }) => {
  const title = language === 'it' ? tip.title_it : tip.title_en;
  const content = language === 'it' ? tip.content_it : tip.content_en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-[#2C2C2E] rounded-2xl overflow-hidden shadow-sm border border-[#3A3A3C] ${isLocked ? 'opacity-60' : ''}`}
    >
      {tip.image_url && (
        <div className="relative h-40 overflow-hidden">
          <img 
            src={tip.image_url} 
            alt={title} 
            className={`w-full h-full object-cover ${isLocked ? 'blur-sm' : ''}`} 
          />
          {isLocked && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white/80" />
            </div>
          )}
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#C9B896]/20 rounded-xl flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-[#C9B896]" />
          </div>
          <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7]">
            {isLocked ? '••••••••' : title}
          </h3>
        </div>
        <p className={`text-[#86868B] text-sm leading-relaxed ${isLocked ? 'blur-sm select-none' : ''}`}>
          {isLocked ? 'Lorem ipsum dolor sit amet consectetur adipiscing elit.' : content}
        </p>
      </div>
    </motion.div>
  );
};

// Preview Itinerary Card
const PreviewItineraryCard = ({ itinerary, language, isLocked = false }) => {
  const title = language === 'it' ? (itinerary?.name_it || itinerary?.title_it) : (itinerary?.name_en || itinerary?.title_en);
  const description = language === 'it' ? itinerary?.description_it : itinerary?.description_en;
  const days = itinerary?.duration_days || itinerary?.days?.length || 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-[#2C2C2E] rounded-2xl overflow-hidden shadow-sm border border-[#3A3A3C] ${isLocked ? 'opacity-60' : ''}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img 
          src={itinerary?.image_url || 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800'} 
          alt={title}
          className={`w-full h-full object-cover ${isLocked ? 'blur-sm' : ''}`}
        />
        <div className="absolute top-3 left-3 bg-[#2C2C2E]/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-[#C9B896]" />
          <span className="text-xs font-medium text-[#F5F5F7]">{days} {language === 'it' ? 'giorni' : 'days'}</span>
        </div>
        {isLocked && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Lock className="w-10 h-10 text-white/80" />
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-lg mb-2">
          {isLocked ? (title || '••••••••') : title}
        </h3>
        <p className={`text-[#86868B] text-sm line-clamp-2 ${isLocked ? 'blur-sm select-none' : ''}`}>
          {isLocked ? 'Acquista la guida per scoprire questo itinerario dettagliato giorno per giorno.' : description}
        </p>
      </div>
    </motion.div>
  );
};

// Preview Checklist
const PreviewChecklist = ({ language }) => {
  const categories = language === 'it' ? [
    { title: 'Cosa Mettere in Valigia', items: ['Giacca impermeabile', 'Scarpe da trekking', '•••••••••'], count: '3/12' },
    { title: 'Documenti Necessari', items: ['Passaporto', '•••••••••'], count: '1/7' },
    { title: 'App da Scaricare', items: ['•••••••••'], count: '0/6' },
    { title: 'Prima di Partire', items: ['•••••••••'], count: '0/6' }
  ] : [
    { title: 'Packing List', items: ['Waterproof jacket', 'Hiking boots', '•••••••••'], count: '3/12' },
    { title: 'Required Documents', items: ['Passport', '•••••••••'], count: '1/7' },
    { title: 'Apps to Download', items: ['•••••••••'], count: '0/6' },
    { title: 'Before Departure', items: ['•••••••••'], count: '0/6' }
  ];

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {categories.map((cat, idx) => (
        <div key={idx} className={`bg-[#2C2C2E] rounded-2xl p-5 border border-[#3A3A3C] ${idx > 0 ? 'opacity-60' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#C9B896]/20 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-[#C9B896]" />
              </div>
              <h3 className="font-semibold text-[#F5F5F7]">{cat.title}</h3>
            </div>
            <span className="text-[#86868B] text-sm">{cat.count}</span>
          </div>
          <div className="space-y-2">
            {cat.items.map((item, i) => (
              <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${item.includes('•') ? 'opacity-50' : 'bg-[#1C1C1E]'}`}>
                <div className={`w-5 h-5 rounded flex items-center justify-center ${item.includes('•') ? 'bg-[#3A3A3C]' : 'bg-green-500'}`}>
                  {!item.includes('•') && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <span className={`text-sm ${item.includes('•') ? 'text-[#86868B] blur-sm' : 'text-[#F5F5F7]'}`}>{item}</span>
              </div>
            ))}
          </div>
          {idx > 0 && (
            <div className="flex items-center justify-center gap-2 mt-4 text-[#86868B] text-sm">
              <Lock className="w-4 h-4" />
              <span>{language === 'it' ? 'Sblocca con la guida completa' : 'Unlock with full guide'}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Preview Map
const PreviewMap = ({ language }) => {
  return (
    <div className="relative bg-[#2C2C2E] rounded-2xl overflow-hidden border border-[#3A3A3C]">
      <div className="h-[400px] relative">
        <img 
          src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=1200" 
          alt="Map Preview"
          className="w-full h-full object-cover blur-sm"
        />
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
          <div className="w-20 h-20 bg-[#C9B896]/20 rounded-full flex items-center justify-center mb-4">
            <MapIcon className="w-10 h-10 text-[#C9B896]" />
          </div>
          <h3 className="text-white text-xl font-semibold mb-2">
            {language === 'it' ? 'Mappa Interattiva' : 'Interactive Map'}
          </h3>
          <p className="text-[#86868B] text-center max-w-md px-6">
            {language === 'it' 
              ? 'Visualizza tutti i luoghi sulla mappa con la guida completa. Filtra per categoria, vedi le distanze e pianifica i tuoi spostamenti.'
              : 'View all locations on the map with the full guide. Filter by category, see distances and plan your movements.'}
          </p>
          <div className="flex items-center gap-2 mt-6 text-[#C9B896]">
            <Lock className="w-5 h-5" />
            <span className="font-medium">{language === 'it' ? 'Disponibile nella guida completa' : 'Available in full guide'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const GuidePreview = () => {
  const { guideId } = useParams();
  const { language } = useLanguage();
  const [guide, setGuide] = useState(null);
  const [preview, setPreview] = useState(null);
  const [gumroadUrl, setGumroadUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('places');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [guideRes, previewRes, gumroadRes] = await Promise.all([
          axios.get(`${API}/guides/${guideId}`),
          axios.get(`${API}/guide-content/${guideId}`),
          axios.get(`${API}/gumroad/link/${guideId}`)
        ]);
        
        setGuide(guideRes.data);
        setPreview(previewRes.data);
        setGumroadUrl(gumroadRes.data.gumroad_url || '');
        
        // Send readable page view to Google Analytics
        if (window.gtag) {
          window.gtag('event', 'page_view', {
            page_title: `Preview - ${guideId}`,
            page_location: `${window.location.origin}/preview/${guideId}`,
            page_path: `/preview/${guideId}`
          });
        }
      } catch (error) {
        console.error('Error fetching preview:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [guideId]);

  const texts = {
    it: {
      freePreview: "Anteprima Gratuita",
      unlockGuide: "Sblocca la Guida Completa",
      map: "Mappa",
      places: "Luoghi",
      itineraries: "Itinerari",
      tips: "Tips",
      checklist: "Checklist",
      previewPlaces: "Luoghi in Anteprima",
      previewTips: "Tips in Anteprima",
      previewItineraries: "Itinerari in Anteprima",
      lockedContent: "Contenuto Bloccato",
      moreLockedPlaces: "altri luoghi da scoprire",
      moreLockedTips: "altri consigli esclusivi",
      moreLockedItineraries: "altri itinerari dettagliati",
      whatYouGet: "Cosa Ottieni con la Guida Completa",
      features: [
        "Accesso a tutti i luoghi con descrizioni dettagliate",
        "Itinerari giornalieri con orari e suggerimenti",
        "Tips insider per evitare errori comuni",
        "Mappe interattive con tutti i punti di interesse",
        "Checklist interattive per organizzare il viaggio",
        "Export PDF per uso offline"
      ],
      buyNow: "Acquista Ora",
      price: "Prezzo"
    },
    en: {
      freePreview: "Free Preview",
      unlockGuide: "Unlock the Complete Guide",
      map: "Map",
      places: "Places",
      itineraries: "Itineraries",
      tips: "Tips",
      checklist: "Checklist",
      previewPlaces: "Preview Places",
      previewTips: "Preview Tips",
      previewItineraries: "Preview Itineraries",
      lockedContent: "Locked Content",
      moreLockedPlaces: "more places to discover",
      moreLockedTips: "more exclusive tips",
      moreLockedItineraries: "more detailed itineraries",
      whatYouGet: "What You Get with the Complete Guide",
      features: [
        "Access to all places with detailed descriptions",
        "Daily itineraries with schedules and suggestions",
        "Insider tips to avoid common mistakes",
        "Interactive maps with all points of interest",
        "Interactive checklists to organize your trip",
        "PDF export for offline use"
      ],
      buyNow: "Buy Now",
      price: "Price"
    }
  };

  const t = texts[language] || texts.it;

  const tabs = [
    { id: 'map', label: t.map, icon: MapIcon },
    { id: 'places', label: t.places, count: preview?.places_count },
    { id: 'itineraries', label: t.itineraries, count: preview?.itineraries_count },
    { id: 'tips', label: t.tips, count: preview?.tips_count },
    { id: 'checklist', label: t.checklist, icon: ClipboardList }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0C0E] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#C9B896] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!guide || !preview) {
    return (
      <div className="min-h-screen bg-[#0C0C0E] flex items-center justify-center">
        <p className="text-[#86868B]">Guide not found</p>
      </div>
    );
  }

  const title = language === 'it' ? guide.title_it : guide.title_en;
  const description = language === 'it' ? guide.description_it : guide.description_en;
  const lockedPlacesCount = preview.places_count - (preview.preview_places?.length || 0);
  const lockedTipsCount = preview.tips_count - (preview.preview_tips?.length || 0);
  const lockedItinerariesCount = preview.itineraries_count - 1;

  return (
    <div className="min-h-screen bg-[#0C0C0E] pt-20">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Guide Info */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 bg-[#C9B896]/20 text-[#C9B896] px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Star className="w-4 h-4" />
                {t.freePreview}
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="font-['Manrope'] font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6"
              >
                {title}
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-[#86868B] text-lg mb-8"
              >
                {description}
              </motion.p>
              
              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex gap-6 mb-8"
              >
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#C9B896]">{preview.places_count}</p>
                  <p className="text-[#86868B] text-sm">{t.places}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#C9B896]">{preview.itineraries_count}</p>
                  <p className="text-[#86868B] text-sm">{t.itineraries}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#C9B896]">{preview.tips_count}</p>
                  <p className="text-[#86868B] text-sm">{t.tips}</p>
                </div>
              </motion.div>

              {/* CTA Button */}
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                href={gumroadUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#C9B896] hover:bg-[#A89770] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-[#C9B896]/30"
                data-testid="buy-now-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                {t.buyNow} - €{guide.price}
              </motion.a>
            </div>
            
            {/* Right: Guide Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img 
                src={guide.image_url} 
                alt={title}
                className="w-full h-[400px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Tabs */}
      <section className="sticky top-16 md:top-20 bg-[#1C1C1E] border-b border-[#3A3A3C] z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 md:px-6 py-4 font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id 
                    ? 'text-[#C9B896]' 
                    : 'text-[#86868B] hover:text-[#F5F5F7]'
                }`}
              >
                {tab.label}
                {tab.count && (
                  <span className="ml-2 text-sm bg-[#3A3A3C] px-2 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
                {activeTab === tab.id && (
                  <motion.div 
                    layoutId="previewActiveTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C9B896]" 
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          
          {/* Map Tab */}
          {activeTab === 'map' && (
            <div className="animate-fade-in">
              <PreviewMap language={language} />
            </div>
          )}

          {/* Places Tab */}
          {activeTab === 'places' && (
            <div className="animate-fade-in">
              {/* Sample places using full expandable card */}
              {preview.preview_places?.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-[#C9B896]/20 text-[#C9B896] text-xs font-medium rounded-full">
                      {language === 'it' ? 'ANTEPRIMA GRATUITA' : 'FREE PREVIEW'}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {preview.preview_places.map((place, idx) => (
                      <PlaceCard key={idx} place={place} language={language} />
                    ))}
                  </div>
                </div>
              )}
              {/* Locked places */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {[...Array(3)].map((_, idx) => (
                  <PreviewPlaceCard 
                    key={`locked-${idx}`} 
                    place={{
                      name_it: 'Luogo Bloccato',
                      name_en: 'Locked Place',
                      description_it: 'Acquista la guida per scoprire questo luogo.',
                      description_en: 'Purchase the guide to discover this place.',
                      category: 'sightseeing',
                      image_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800'
                    }} 
                    language={language} 
                    isLocked={true} 
                  />
                ))}
              </div>
              <div className="flex items-center justify-center gap-3 text-[#86868B]">
                <Lock className="w-5 h-5" />
                <span>+{lockedPlacesCount} {t.moreLockedPlaces}</span>
              </div>
            </div>
          )}

          {/* Itineraries Tab */}
          {activeTab === 'itineraries' && (
            <div className="animate-fade-in">
              {/* Sample itinerary using full card */}
              {preview.preview_itineraries?.length > 0 && (
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-[#C9B896]/20 text-[#C9B896] text-xs font-medium rounded-full">
                      {language === 'it' ? 'ANTEPRIMA GRATUITA' : 'FREE PREVIEW'}
                    </span>
                  </div>
                  {preview.preview_itineraries.map((itin, idx) => (
                    <ItineraryCard key={idx} itinerary={itin} language={language} />
                  ))}
                </div>
              )}
              {/* Locked itineraries */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {(preview.locked_itineraries || []).slice(0, 3).map((locked, idx) => (
                  <PreviewItineraryCard 
                    key={`locked-${idx}`} 
                    itinerary={{
                      title_it: locked.name_it || 'Itinerario Bloccato',
                      title_en: locked.name_en || 'Locked Itinerary',
                      name_it: locked.name_it,
                      name_en: locked.name_en,
                      description_it: 'Acquista la guida per accedere a questo itinerario.',
                      description_en: 'Purchase the guide to access this itinerary.',
                      duration_days: locked.duration_days,
                      days: Array.from({length: locked.duration_days || 5}, (_, i) => i + 1)
                    }} 
                    language={language} 
                    isLocked={true} 
                  />
                ))}
              </div>
              {lockedItinerariesCount > 0 && (
                <div className="flex items-center justify-center gap-3 text-[#86868B]">
                  <Lock className="w-5 h-5" />
                  <span>+{lockedItinerariesCount} {t.moreLockedItineraries}</span>
                </div>
              )}
            </div>
          )}

          {/* Tips Tab */}
          {activeTab === 'tips' && (
            <div className="animate-fade-in">
              {/* Sample tips using full card */}
              {preview.preview_tips?.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-[#C9B896]/20 text-[#C9B896] text-xs font-medium rounded-full">
                      {language === 'it' ? 'ANTEPRIMA GRATUITA' : 'FREE PREVIEW'}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {preview.preview_tips.map((tip, idx) => (
                      <TipCard key={idx} tip={tip} language={language} />
                    ))}
                  </div>
                </div>
              )}
              {/* Locked tips */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[...Array(2)].map((_, idx) => (
                  <PreviewTipCard 
                    key={`locked-tip-${idx}`} 
                    tip={{
                      title_it: 'Consiglio Bloccato',
                      title_en: 'Locked Tip',
                      content_it: 'Acquista la guida per accedere a questo consiglio.',
                      content_en: 'Purchase the guide to access this tip.',
                      image_url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800'
                    }} 
                    language={language} 
                    isLocked={true} 
                  />
                ))}
              </div>
              <div className="flex items-center justify-center gap-3 text-[#86868B]">
                <Lock className="w-5 h-5" />
                <span>+{lockedTipsCount} {t.moreLockedTips}</span>
              </div>
            </div>
          )}

          {/* Checklist Tab */}
          {activeTab === 'checklist' && (
            <div className="animate-fade-in">
              <PreviewChecklist language={language} />
              <div className="flex items-center justify-center gap-3 text-[#86868B] mt-8">
                <Lock className="w-5 h-5" />
                <span>{language === 'it' ? 'Checklist completa disponibile con la guida' : 'Full checklist available with the guide'}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1C1C1E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-[#2C2C2E] rounded-3xl p-8 md:p-16 border border-[#3A3A3C]">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left: Features */}
              <div>
                <h2 className="font-['Manrope'] font-semibold text-2xl md:text-3xl text-white mb-8">
                  {t.whatYouGet}
                </h2>
                
                <ul className="space-y-4">
                  {t.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="w-6 h-6 text-[#C9B896] flex-shrink-0 mt-0.5" />
                      <span className="text-[#F5F5F7]">{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
              
              {/* Right: Price & CTA */}
              <div className="text-center md:text-right">
                <p className="text-[#86868B] mb-2">{t.price}</p>
                <p className="text-5xl md:text-6xl font-bold text-white mb-8">
                  €{guide.price}
                </p>
                
                <a
                  href={gumroadUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#C9B896] hover:bg-[#A89770] text-white px-10 py-5 rounded-full text-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-[#C9B896]/30"
                  data-testid="buy-now-cta-btn"
                >
                  <ShoppingBag className="w-6 h-6" />
                  {t.buyNow}
                  <ArrowRight className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuidePreview;
